import React, { useEffect, useMemo, useState } from "react";
import {
  FaCheckCircle,
  FaChevronLeft,
  FaChevronRight,
  FaCopy,
  FaEdit,
  FaPlus,
  FaSearch,
  FaTimes,
  FaTrash,
  FaExclamationTriangle,
  FaSync,
} from "react-icons/fa";
import {
  addTeacher,
  deleteTeacher,
  getTeachers,
  updateTeacher,
} from "../../services/teacherService";
import { getClasses } from "../../services/classService";
import { toast } from "react-toastify";
import "./TeacherLayer.css";

const ErrorToast = ({ message }) => {
  const displayMsg = typeof message === "string" 
    ? message 
    : (message && typeof message === "object" ? Object.values(message).flat().join(", ") : JSON.stringify(message) || "Something went wrong");
    
  return (
    <div className="d-flex align-items-start gap-2">
      <div className="text-danger mt-1 fs-5">
        <FaExclamationTriangle />
      </div>
      <div>
        <div className="fw-bold text-danger mb-1">Action Failed</div>
        <div style={{ fontSize: '13.5px', color: '#444' }}>{displayMsg}</div>
      </div>
    </div>
  );
};

const TeacherLayer = () => {
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    class_ids: [],
  });
  const [search, setSearch] = useState("");
  const [assignmentFilter, setAssignmentFilter] = useState("all");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [classSearch, setClassSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setModal(false);
    setEditId(null);
    setForm({ name: "", email: "", password: "", class_ids: [] });
    setClassSearch("");
  };

  const fetchTeachers = async () => {
    const res = await getTeachers();
    if (res?.status) setTeachers(res.data || []);
  };

  const fetchClasses = async () => {
    const res = await getClasses();
    if (res?.status) setClasses(res.data || []);
  };

  useEffect(() => {
    fetchTeachers();
    fetchClasses();
  }, []);

  const handleCheckbox = (id) => {
    setForm((prev) => {
      const normalizedId = id.toString();
      const exists = prev.class_ids.includes(normalizedId);

      return {
        ...prev,
        class_ids: exists
          ? prev.class_ids.filter((item) => item !== normalizedId)
          : [...prev.class_ids, normalizedId],
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.name || !form.email || !form.password) {
      toast.error("All fields required");
      return;
    }

    setLoading(true);

    try {
      const res = editId ? await updateTeacher(editId, form) : await addTeacher(form);

      const isSuccess = res?.status === true || res?.status === "true" || res?.status === 1 || res?.status === "1";

      if (isSuccess) {
        toast.success(editId ? "Updated successfully" : "Created successfully");

        if (editId) {
          setTeachers((prev) =>
            prev.map((teacher) =>
              teacher.id === editId
                ? {
                    ...teacher,
                    name: form.name,
                    email: form.email,
                    plain_password: form.password,
                    classes_data: classes.filter((item) =>
                      form.class_ids.includes(item.id.toString())
                    ),
                  }
                : teacher
            )
          );
        } else {
          setTeachers((prev) => [
            {
              id: res.data?.id || Date.now(),
              name: form.name,
              email: form.email,
              plain_password: form.password,
              classes_data: classes.filter((item) =>
                form.class_ids.includes(item.id.toString())
              ),
            },
            ...prev,
          ]);
        }

        resetForm();
      } else {
        const message = res?.message || "Something went wrong";
        toast.error(<ErrorToast message={message} />, { icon: false });
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error.message || "Server error";
      toast.error(<ErrorToast message={errorMsg} />, { icon: false });
    }

    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete teacher?")) return;

    const res = await deleteTeacher(id);
    if (res?.status) {
      setTeachers((prev) => prev.filter((teacher) => teacher.id !== id));
      toast.success("Deleted successfully");
    } else {
      toast.error("Delete failed");
    }
  };

  const copyData = (teacher) => {
    const text = `Email: ${teacher.email}\nPassword: ${teacher.plain_password}`;
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  const handleEdit = (teacher) => {
    setForm({
      name: teacher.name,
      email: teacher.email,
      password: teacher.plain_password,
      class_ids: teacher.classes_data?.map((item) => item.id.toString()) || [],
    });
    setEditId(teacher.id);
    setModal(true);
  };

  const filteredTeachers = useMemo(() => {
    return teachers.filter((teacher) => {
      const matchesSearch =
        teacher.name.toLowerCase().includes(search.toLowerCase()) ||
        teacher.email.toLowerCase().includes(search.toLowerCase());

      const isAssigned = (teacher.classes_data?.length || 0) > 0;
      const matchesAssignment =
        assignmentFilter === "all" ||
        (assignmentFilter === "assigned" && isAssigned) ||
        (assignmentFilter === "unassigned" && !isAssigned);

      return matchesSearch && matchesAssignment;
    });
  }, [teachers, search, assignmentFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredTeachers.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedTeachers = filteredTeachers.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    setPage(1);
  }, [search, assignmentFilter, pageSize]);

  const visibleClasses = classes
    .filter((item) => (!editId ? item.status === "new" : true))
    .filter((item) => item.class_name.toLowerCase().includes(classSearch.toLowerCase()));

  return (
    <div className="school-wrapper">
      <div className="school-card smooth-card">
        <div className="admin-page-shell">
          <div className="admin-page-header list-header d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3">
              <h6 className="fw-bold text-primary mb-0 d-flex align-items-center gap-2 text-uppercase" style={{ letterSpacing: '0.5px', fontSize: '15px' }}>
                <div style={{ width: '6px', height: '18px', backgroundColor: '#4f46e5', borderRadius: '3px' }}></div>
                Teacher List
              </h6>

            <div className="d-flex flex-wrap align-items-center gap-2">
              <button className="btn btn-light border d-flex align-items-center justify-content-center p-2 rounded-circle shadow-sm" onClick={fetchTeachers} style={{ width: '40px', height: '40px' }} title="Refresh">
                <FaSync className="text-secondary" />
              </button>
              <button className="btn-primary" onClick={() => setModal(true)}>
                <FaPlus /> Add Teacher
              </button>
            </div>
          </div>

          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-6 col-lg-4">
              <label className="fw-semibold text-muted d-block mb-2">Search</label>
              <div className="admin-search search-box">
                <FaSearch />
                <input
                  placeholder="Search by name or email"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-4">
              <label className="fw-semibold text-muted d-block mb-2">Status</label>
              <select
                className="admin-filter w-100"
                value={assignmentFilter}
                onChange={(event) => setAssignmentFilter(event.target.value)}
              >
                <option value="all">All</option>
                <option value="assigned">Assigned</option>
                <option value="unassigned">Unassigned</option>
              </select>
            </div>

            <div className="col-12 col-md-6 col-lg-4">
              <label className="fw-semibold text-muted d-block mb-2">Show</label>
              <select
                className="admin-select w-100"
                value={pageSize}
                onChange={(event) => setPageSize(Number(event.target.value))}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          <div className="teacher-table-shell admin-table-card admin-table-scroll admin-responsive-table">
            <table className="user-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Password</th>
                  <th>Classes</th>
                  <th>Copy</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {paginatedTeachers.length ? (
                  paginatedTeachers.map((teacher, index) => (
                    <tr key={teacher.id} className="smooth-table-row">
                      <td data-label="No.">{startIndex + index + 1}</td>
                      <td data-label="Name">{teacher.name}</td>
                      <td data-label="Email">{teacher.email}</td>
                      <td data-label="Password">{teacher.plain_password}</td>
                      <td data-label="Classes">
                        {teacher.classes_data?.length
                          ? teacher.classes_data.map((item) => item.class_name).join(", ")
                          : "Not Assigned"}
                      </td>
                      <td data-label="Copy">
                        <button
                          type="button"
                          className="table-icon-btn"
                          onClick={() => copyData(teacher)}
                          aria-label="Copy credentials"
                        >
                          <FaCopy />
                        </button>
                      </td>
                      <td data-label="Action" className="actions">
                        <button
                          type="button"
                          className="table-icon-btn"
                          onClick={() => handleEdit(teacher)}
                          aria-label="Edit teacher"
                        >
                          <FaEdit />
                        </button>
                        <button
                          type="button"
                          className="table-icon-btn danger"
                          onClick={() => handleDelete(teacher.id)}
                          aria-label="Delete teacher"
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="empty-row">
                      No teachers found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="admin-pagination">
            <div className="admin-pagination-info">
              Showing {filteredTeachers.length ? startIndex + 1 : 0} to {Math.min(startIndex + pageSize, filteredTeachers.length)} of {filteredTeachers.length} teachers
            </div>

            <div className="admin-pagination-controls">
              <button
                type="button"
                className="admin-page-btn"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <FaChevronLeft />
              </button>

              {Array.from({ length: totalPages }, (_, index) => index + 1)
                .slice(0, 5)
                .map((pageNumber) => (
                  <button
                    type="button"
                    key={pageNumber}
                    className={`admin-page-btn ${currentPage === pageNumber ? "active" : ""}`}
                    onClick={() => setPage(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                ))}

              <button
                type="button"
                className="admin-page-btn"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                <FaChevronRight />
              </button>
            </div>
          </div>
        </div>
      </div>

      {modal && (
        <div className="modal-overlay">
          <div className="modal-box smooth-modal">
            <div className="teacher-modal-header">
              <div>
                <h4>{editId ? "Update Teacher" : "Add Teacher"}</h4>
                <p>Complete the form and select classes for this teacher.</p>
              </div>
              <button type="button" className="modal-close-btn" onClick={resetForm}>
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="teacher_name">Name</label>
                <input
                  id="teacher_name"
                  placeholder="Enter name"
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </div>

              <div className="form-group">
                <label htmlFor="teacher_email">Email</label>
                <input
                  id="teacher_email"
                  type="email"
                  value={form.email}
                  onChange={(event) => setForm({ ...form, email: event.target.value })}
                />
              </div>

              <div className="form-group">
                <label htmlFor="teacher_password">Password</label>
                <input
                  id="teacher_password"
                  value={form.password}
                  onChange={(event) => setForm({ ...form, password: event.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Assign Classes</label>

                <div className="class-selector">
                  <div className="class-search">
                    <FaSearch />
                    <input
                      placeholder="Search class..."
                      value={classSearch}
                      onChange={(event) => setClassSearch(event.target.value)}
                    />
                  </div>

                  <div className="selected-count">
                    Selected Classes: {form.class_ids.length}
                  </div>

                  <div className="class-list">
                    {visibleClasses.length ? (
                      visibleClasses.map((item) => {
                        const selected = form.class_ids.includes(item.id.toString());

                        return (
                          <div
                            key={item.id}
                            className={`class-item ${selected ? "active" : ""}`}
                            onClick={() => handleCheckbox(item.id)}
                          >
                            <span>{item.class_name}</span>
                            {selected && <FaCheckCircle className="check-icon" />}
                          </div>
                        );
                      })
                    ) : (
                      <div className="class-empty-state">No classes available</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="d-flex flex-column flex-sm-row gap-3 mt-4">
                <button type="button" className="btn-secondary flex-grow-1 m-0 text-center" onClick={resetForm}>
                  Cancel
                </button>
                <button className="btn-primary flex-grow-1 m-0" disabled={loading}>
                  {loading ? "Please wait..." : editId ? "Update" : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherLayer;



// import React, { useEffect, useMemo, useState } from "react";
// import {
//   FaCheckCircle,
//   FaChevronLeft,
//   FaChevronRight,
//   FaCopy,
//   FaEdit,
//   FaPlus,
//   FaSearch,
//   FaTimes,
//   FaTrash,
// } from "react-icons/fa";
// import {
//   addTeacher,
//   deleteTeacher,
//   getTeachers,
//   updateTeacher,
// } from "../../services/teacherService";
// import { getClasses } from "../../services/classService";
// import { toast } from "react-toastify";
// import "./TeacherLayer.css";

// const TeacherLayer = () => {
//   const [teachers, setTeachers] = useState([]);
//   const [classes, setClasses] = useState([]);
//   const [modal, setModal] = useState(false);
//   const [editId, setEditId] = useState(null);
//   const [form, setForm] = useState({
//     name: "",
//     email: "",
//     password: "",
//     class_id: "", // Changed from class_ids: []
//   });
//   const [search, setSearch] = useState("");
//   const [assignmentFilter, setAssignmentFilter] = useState("all");
//   const [pageSize, setPageSize] = useState(10);
//   const [page, setPage] = useState(1);
//   const [classSearch, setClassSearch] = useState("");
//   const [loading, setLoading] = useState(false);

//   const resetForm = () => {
//     setModal(false);
//     setEditId(null);
//     setForm({ name: "", email: "", password: "", class_id: "" }); // Reset to empty string
//     setClassSearch("");
//   };

//   const fetchTeachers = async () => {
//     const res = await getTeachers();
//     if (res?.status) setTeachers(res.data || []);
//   };

//   const fetchClasses = async () => {
//     const res = await getClasses();
//     if (res?.status) setClasses(res.data || []);
//   };

//   useEffect(() => {
//     fetchTeachers();
//     fetchClasses();
//   }, []);

//   const handleSelectClass = (id) => {
//     const normalizedId = id.toString();
//     setForm((prev) => ({
//       ...prev,
//       // If clicking the already selected class, deselect it (set to "").
//       // Otherwise, select the new class (replacing any previous selection).
//       class_id: prev.class_id === normalizedId ? "" : normalizedId,
//     }));
//   };

//   const handleSubmit = async (event) => {
//     event.preventDefault();

//     if (!form.name || !form.email || !form.password) {
//       toast.error("All fields required");
//       return;
//     }

//     setLoading(true);

//     // Convert single class_id to an array for API compatibility (assuming API expects an array).
//     const payload = {
//       ...form,
//       class_ids: form.class_id ? [form.class_id] : [],
//     };
//     // Remove the temporary single class_id field from payload if your API doesn't ignore extra fields
//     delete payload.class_id;

//     try {
//       const res = editId
//         ? await updateTeacher(editId, payload)
//         : await addTeacher(payload);

//       if (res?.status) {
//         toast.success(editId ? "Updated successfully" : "Created successfully");

//         // Find the selected class object to update local state immediately.
//         const selectedClassData = classes.filter(
//           (item) => item.id.toString() === form.class_id
//         );

//         if (editId) {
//           setTeachers((prev) =>
//             prev.map((teacher) =>
//               teacher.id === editId
//                 ? {
//                     ...teacher,
//                     name: form.name,
//                     email: form.email,
//                     plain_password: form.password,
//                     // Use the singular selected class data, or empty array if none selected.
//                     classes_data: selectedClassData,
//                   }
//                 : teacher
//             )
//           );
//         } else {
//           setTeachers((prev) => [
//             {
//               id: res.data?.id || Date.now(),
//               name: form.name,
//               email: form.email,
//               plain_password: form.password,
//               classes_data: selectedClassData,
//             },
//             ...prev,
//           ]);
//         }

//         resetForm();
//       } else {
//         toast.error(res?.message || "Something went wrong");
//       }
//     } catch {
//       toast.error("Server error");
//     }

//     setLoading(false);
//   };

//   const handleDelete = async (id) => {
//     if (!window.confirm("Delete teacher?")) return;

//     const res = await deleteTeacher(id);
//     if (res?.status) {
//       setTeachers((prev) => prev.filter((teacher) => teacher.id !== id));
//       toast.success("Deleted successfully");
//     } else {
//       toast.error("Delete failed");
//     }
//   };

//   const copyData = (teacher) => {
//     const text = `Email: ${teacher.email}\nPassword: ${teacher.plain_password}`;
//     navigator.clipboard.writeText(text);
//     toast.success("Copied");
//   };

//   const handleEdit = (teacher) => {
//     // Get the first class ID from the array, or empty string if unassigned.
//     const firstClassId = teacher.classes_data?.[0]?.id?.toString() || "";
//     setForm({
//       name: teacher.name,
//       email: teacher.email,
//       password: teacher.plain_password,
//       class_id: firstClassId,
//     });
//     setEditId(teacher.id);
//     setModal(true);
//   };

//   const filteredTeachers = useMemo(() => {
//     return teachers.filter((teacher) => {
//       const matchesSearch =
//         teacher.name.toLowerCase().includes(search.toLowerCase()) ||
//         teacher.email.toLowerCase().includes(search.toLowerCase());

//       const isAssigned = (teacher.classes_data?.length || 0) > 0;
//       const matchesAssignment =
//         assignmentFilter === "all" ||
//         (assignmentFilter === "assigned" && isAssigned) ||
//         (assignmentFilter === "unassigned" && !isAssigned);

//       return matchesSearch && matchesAssignment;
//     });
//   }, [teachers, search, assignmentFilter]);

//   const totalPages = Math.max(1, Math.ceil(filteredTeachers.length / pageSize));
//   const currentPage = Math.min(page, totalPages);
//   const startIndex = (currentPage - 1) * pageSize;
//   const paginatedTeachers = filteredTeachers.slice(
//     startIndex,
//     startIndex + pageSize
//   );

//   useEffect(() => {
//     setPage(1);
//   }, [search, assignmentFilter, pageSize]);

//   const visibleClasses = classes
//     .filter((item) => (!editId ? item.status === "new" : true))
//     .filter((item) =>
//       item.class_name.toLowerCase().includes(classSearch.toLowerCase())
//     );

//   return (
//     <div className="school-wrapper">
//       <div className="school-card smooth-card">
//         <div className="admin-page-shell">
//           <div className="admin-page-header list-header">
//             <div>
//               <h5 className="admin-page-title">Teacher List</h5>
//             </div>

//             <button className="btn-primary" onClick={() => setModal(true)}>
//               <FaPlus /> Add Teacher
//             </button>
//           </div>

//           <div className="row g-3 align-items-end">
//             <div className="col-12 col-md-6 col-lg-4">
//               <label className="fw-semibold text-muted d-block mb-2">
//                 Search
//               </label>
//               <div className="admin-search search-box">
//                 <FaSearch />
//                 <input
//                   placeholder="Search by name or email"
//                   value={search}
//                   onChange={(event) => setSearch(event.target.value)}
//                 />
//               </div>
//             </div>

//             <div className="col-12 col-md-6 col-lg-4">
//               <label className="fw-semibold text-muted d-block mb-2">
//                 Status
//               </label>
//               <select
//                 className="admin-filter w-100"
//                 value={assignmentFilter}
//                 onChange={(event) => setAssignmentFilter(event.target.value)}
//               >
//                 <option value="all">All</option>
//                 <option value="assigned">Assigned</option>
//                 <option value="unassigned">Unassigned</option>
//               </select>
//             </div>

//             <div className="col-12 col-md-6 col-lg-4">
//               <label className="fw-semibold text-muted d-block mb-2">
//                 Show
//               </label>
//               <select
//                 className="admin-select w-100"
//                 value={pageSize}
//                 onChange={(event) => setPageSize(Number(event.target.value))}
//               >
//                 <option value={5}>5</option>
//                 <option value={10}>10</option>
//                 <option value={20}>20</option>
//               </select>
//             </div>
//           </div>

//           <div className="teacher-table-shell admin-table-card admin-table-scroll admin-responsive-table">
//             <table className="user-table">
//               <thead>
//                 <tr>
//                   <th>#</th>
//                   <th>Name</th>
//                   <th>Email</th>
//                   <th>Password</th>
//                   <th>Class</th> {/* Changed from Classes to Class */}
//                   <th>Copy</th>
//                   <th>Action</th>
//                 </tr>
//               </thead>

//               <tbody>
//                 {paginatedTeachers.length ? (
//                   paginatedTeachers.map((teacher, index) => (
//                     <tr key={teacher.id} className="smooth-table-row">
//                       <td data-label="No.">{startIndex + index + 1}</td>
//                       <td data-label="Name">{teacher.name}</td>
//                       <td data-label="Email">{teacher.email}</td>
//                       <td data-label="Password">{teacher.plain_password}</td>
//                       <td data-label="Class">
//                         {" "}
//                         {/* Changed from Classes to Class */}
//                         {teacher.classes_data?.length
//                           ? teacher.classes_data[0].class_name // Only show the first (and only) class
//                           : "Not Assigned"}
//                       </td>
//                       <td data-label="Copy">
//                         <button
//                           type="button"
//                           className="table-icon-btn"
//                           onClick={() => copyData(teacher)}
//                           aria-label="Copy credentials"
//                         >
//                           <FaCopy />
//                         </button>
//                       </td>
//                       <td data-label="Action" className="actions">
//                         <button
//                           type="button"
//                           className="table-icon-btn"
//                           onClick={() => handleEdit(teacher)}
//                           aria-label="Edit teacher"
//                         >
//                           <FaEdit />
//                         </button>
//                         <button
//                           type="button"
//                           className="table-icon-btn danger"
//                           onClick={() => handleDelete(teacher.id)}
//                           aria-label="Delete teacher"
//                         >
//                           <FaTrash />
//                         </button>
//                       </td>
//                     </tr>
//                   ))
//                 ) : (
//                   <tr>
//                     <td colSpan="7" className="empty-row">
//                       No teachers found
//                     </td>
//                   </tr>
//                 )}
//               </tbody>
//             </table>
//           </div>

//           <div className="admin-pagination">
//             <div className="admin-pagination-info">
//               Showing {filteredTeachers.length ? startIndex + 1 : 0} to{" "}
//               {Math.min(startIndex + pageSize, filteredTeachers.length)} of{" "}
//               {filteredTeachers.length} teachers
//             </div>

//             <div className="admin-pagination-controls">
//               <button
//                 type="button"
//                 className="admin-page-btn"
//                 onClick={() => setPage((prev) => Math.max(1, prev - 1))}
//                 disabled={currentPage === 1}
//               >
//                 <FaChevronLeft />
//               </button>

//               {Array.from({ length: totalPages }, (_, index) => index + 1)
//                 .slice(0, 5)
//                 .map((pageNumber) => (
//                   <button
//                     type="button"
//                     key={pageNumber}
//                     className={`admin-page-btn ${
//                       currentPage === pageNumber ? "active" : ""
//                     }`}
//                     onClick={() => setPage(pageNumber)}
//                   >
//                     {pageNumber}
//                   </button>
//                 ))}

//               <button
//                 type="button"
//                 className="admin-page-btn"
//                 onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
//                 disabled={currentPage === totalPages}
//               >
//                 <FaChevronRight />
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>

//       {modal && (
//         <div className="modal-overlay">
//           <div className="modal-box smooth-modal">
//             <div className="teacher-modal-header">
//               <div>
//                 <h4>{editId ? "Update Teacher" : "Add Teacher"}</h4>
//                 <p>Complete the form and select a class for this teacher.</p>
//               </div>
//               <button
//                 type="button"
//                 className="modal-close-btn"
//                 onClick={resetForm}
//               >
//                 <FaTimes />
//               </button>
//             </div>

//             <form onSubmit={handleSubmit}>
//               <div className="form-group">
//                 <label htmlFor="teacher_name">Name</label>
//                 <input
//                   id="teacher_name"
//                   placeholder="Enter name"
//                   value={form.name}
//                   onChange={(event) =>
//                     setForm({ ...form, name: event.target.value })
//                   }
//                 />
//               </div>

//               <div className="form-group">
//                 <label htmlFor="teacher_email">Email</label>
//                 <input
//                   id="teacher_email"
//                   type="email"
//                   value={form.email}
//                   onChange={(event) =>
//                     setForm({ ...form, email: event.target.value })
//                   }
//                 />
//               </div>

//               <div className="form-group">
//                 <label htmlFor="teacher_password">Password</label>
//                 <input
//                   id="teacher_password"
//                   value={form.password}
//                   onChange={(event) =>
//                     setForm({ ...form, password: event.target.value })
//                   }
//                 />
//               </div>

//               <div className="form-group">
//                 <label>Assign Class</label>

//                 <div className="class-selector">
//                   <div className="class-search">
//                     <FaSearch />
//                     <input
//                       placeholder="Search class..."
//                       value={classSearch}
//                       onChange={(event) => setClassSearch(event.target.value)}
//                     />
//                   </div>

//                   <div className="class-list">
//                     {visibleClasses.length ? (
//                       visibleClasses.map((item) => {
//                         const selected =
//                           form.class_id === item.id.toString();

//                         return (
//                           <div
//                             key={item.id}
//                             className={`class-item ${selected ? "active" : ""}`}
//                             // Changed handleCheckbox to handleSelectClass for single selection logic
//                             onClick={() => handleSelectClass(item.id)}
//                           >
//                             <span>{item.class_name}</span>
//                             {selected && (
//                               <FaCheckCircle className="check-icon" />
//                             )}
//                           </div>
//                         );
//                       })
//                     ) : (
//                       <div className="class-empty-state">
//                         No classes available
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               </div>

//               <div className="form-actions">
//                 <button className="btn-primary" disabled={loading}>
//                   {loading ? "Please wait..." : editId ? "Update" : "Add"}
//                 </button>

//                 <button
//                   type="button"
//                   className="btn-secondary text-center"
//                   onClick={resetForm}
//                 >
//                   Cancel
//                 </button>
//               </div>
//             </form>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default TeacherLayer;

